import { clearCustomerDrafts } from "../Utils/customerDrafts";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import i18n from "../i18n";
import { currentReturnPath, loginUrlForReturnPath } from "../Login/authRouting";

interface User {
  id: number;
  username: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  profile?: string;
  role?: string;
  bio?: string;
  language?: string;
  subscribed?: boolean;
  user_type?: string;
  is_attorney?: boolean;
  // S.1: matter detail layout preference. 'asymmetric' (default, variant B)
  // or 'timeline' (variant D). MatterDetailPage reads this to render the
  // chosen layout. Settings UI lives at /profile/preferences (S.2).
  matter_detail_layout?: "asymmetric" | "timeline";

  // V1 multi-workspace: the user's currently active workspace. Set on every
  // authed response; readers (matters list, clients, etc.) scope by it.
  // After workspace switch, UserContext.refetchUser() reloads this. Legacy
  // organization_id + org_role were dropped in V1.5.
  active_org_id?: string | null;
  // Kind of the active workspace, from /api/user. Replaces the dropped
  // organization_id for "is this a firm or a personal workspace" checks.
  active_org_kind?: "firm" | "personal" | null;

  template?: string | null;
  setTemplate?: (template: string | null) => void;
}

interface UserContextValue {
  user: User | null;
  setUser: (user: User | null) => void;
  refetchUser: () => void;
  template: string | null;
  setTemplate: (template: string | null) => void;
  isAuthLoading: boolean;
  authError?: string;
}

const UserContext = createContext<UserContextValue>({
  user: null,
  setUser: () => {},
  refetchUser: () => {},
  template: null,
  setTemplate: () => {},
  isAuthLoading: true,
});

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};

interface UserProviderProps {
  children: React.ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [template, setTemplate] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const ws = useRef<WebSocket | null>(null);

  const refetchUser = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/user`,
        {
          credentials: "include",
        }
      );

      // Check if the response is successful before parsing
      if (!response.ok) {
        // Only an authentication failure invalidates known user state. A
        // transient backend or authorization error must not masquerade as a
        // logout.
        if (response.status === 401) {
          setAuthError("");
          clearCustomerDrafts({ preserveGuest: !user });
          setUser(null);
        } else {
          setAuthError(
            "The server could not verify your session. Retry the connection; this is not a confirmed sign-out."
          );
          console.error(`Unable to refresh user: HTTP ${response.status}`);
        }
        return;
      }

      const userData = await response.json();
      setAuthError("");
      setUser(userData);
      // Sync language preference from DB to i18n
      if (userData.language && userData.language !== i18n.language) {
        i18n.changeLanguage(userData.language);
      }
    } catch (error) {
      setAuthError(
        "The server could not be reached to verify your session. Retry the connection."
      );
      // Preserve known user state during transient network failures.
      console.error("Error fetching user data:", error);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const setupWebSocket = useCallback(() => {
    if (!user || ws.current) return;

    // The backend is at www.api.leegal.ai, and the WebSocket server is there too.
    const backendUrlString =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:3201";

    // Construct the WebSocket URL from the backend URL, explicitly handling http and https.
    const wsUrl = backendUrlString.startsWith("https://")
      ? backendUrlString.replace("https://", "wss://")
      : backendUrlString.replace("http://", "ws://");

    const socket = new WebSocket(wsUrl);
    ws.current = socket;

    socket.onopen = () => {
      // Authenticate the connection
      if (user?.id) {
        socket.send(JSON.stringify({ type: "auth", userId: user.id }));
      }
    };

    socket.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        // V6.D — notification push. Route to anyone listening via the
        // global window event so the notifications hook can refresh
        // the badge instantly without holding a ref to this socket.
        if (data?.type === "notification_new") {
          window.dispatchEvent(
            new CustomEvent("notification_new", { detail: data })
          );
        }
        if (data?.type === "matter-dashboard-changed") {
          window.dispatchEvent(
            new CustomEvent("matter-dashboard-changed", { detail: data })
          );
        }
      } catch {
        // ignore malformed messages
      }
    };

    socket.onclose = () => {
      ws.current = null;
      // Simple exponential backoff reconnect strategy
      setTimeout(() => {
        if (!ws.current) {
          // Check if it's already reconnected
          setupWebSocket();
        }
      }, 3000);
    };

    socket.onerror = error => {
      // console.error('WebSocket error:', error);
      socket.close(); // This will trigger the onclose handler for reconnection
    };
  }, [user]);

  // Initialize user data
  useEffect(() => {
    refetchUser();
  }, []);

  // Handle 401 responses (from axios interceptor) and inactivity separately.
  // This must live here (inside UserProvider) so setUser is the real state setter,
  // not the no-op default from createContext.
  useEffect(() => {
    const handleUnauthorized = () => {
      clearCustomerDrafts();
      setUser(null);
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = loginUrlForReturnPath(currentReturnPath(), {
          authenticationRequired: true,
        });
      }
    };

    const handleSessionExpired = () => {
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = loginUrlForReturnPath(currentReturnPath(), {
          expired: true,
        });
      }
    };

    window.addEventListener("unauthorized", handleUnauthorized);
    window.addEventListener("session-expired", handleSessionExpired);

    return () => {
      window.removeEventListener("unauthorized", handleUnauthorized);
      window.removeEventListener("session-expired", handleSessionExpired);
    };
  }, []);

  // Inactivity timeout: auto-logout after inactivity
  // Fires slightly before the backend session expires (2h) to ensure
  // the user sees a clean "session expired" message instead of a 401 error.
  // Also sends periodic heartbeats to keep the backend session alive while
  // the user is active (e.g., waiting on a long task with occasional mouse moves).
  // Activity is shared across tabs via localStorage so an idle tab won't
  // kill the session while the user is active in another tab.
  useEffect(() => {
    if (!user) return;

    const INACTIVITY_TIMEOUT = 115 * 60 * 1000; // 1h 55m (backend session is 2h)
    const HEARTBEAT_INTERVAL = 30 * 60 * 1000; // 30 min
    const THROTTLE_MS = 60 * 1000; // Throttle activity resets to once per minute
    const STORAGE_KEY = "session_last_activity";
    let timeoutId: ReturnType<typeof setTimeout>;
    let heartbeatId: ReturnType<typeof setInterval>;
    let lastReset = 0;

    const getLastActivity = (): number => {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? parseInt(stored, 10) : Date.now();
    };

    const setLastActivity = (now: number) => {
      localStorage.setItem(STORAGE_KEY, String(now));
    };

    const sendHeartbeat = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user`, {
        credentials: "include",
      }).catch(() => {});
    };

    const doLogout = async () => {
      // Before logging out, check if another tab was active recently
      const timeSinceActivity = Date.now() - getLastActivity();
      if (timeSinceActivity < INACTIVITY_TIMEOUT) {
        // Another tab had recent activity — reschedule instead of logging out
        scheduleTimeout(INACTIVITY_TIMEOUT - timeSinceActivity);
        return;
      }
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logout`, {
          method: "POST",
          credentials: "include",
        });
      } catch (_) {
        // Session may already be expired server-side
      }
      localStorage.removeItem(STORAGE_KEY);
      clearCustomerDrafts();
      setUser(null);
      window.dispatchEvent(new CustomEvent("session-expired"));
    };

    const scheduleTimeout = (delay: number) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(doLogout, delay);
    };

    const resetTimer = () => {
      const now = Date.now();
      // Throttle: skip if we reset less than a minute ago (avoids excessive
      // clearTimeout/setTimeout during rapid mousemove/scroll/typing)
      if (now - lastReset < THROTTLE_MS) return;
      lastReset = now;
      setLastActivity(now);
      scheduleTimeout(INACTIVITY_TIMEOUT);
    };

    // When another tab updates lastActivity in localStorage, reset this tab's timer
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        lastReset = Date.now();
        scheduleTimeout(INACTIVITY_TIMEOUT);
      }
    };

    // Periodically ping the backend if the user has been active recently,
    // so the server-side session doesn't expire during long-running tasks.
    heartbeatId = setInterval(() => {
      if (Date.now() - getLastActivity() < INACTIVITY_TIMEOUT) {
        sendHeartbeat();
      }
    }, HEARTBEAT_INTERVAL);

    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
    ] as const;
    events.forEach(e => window.addEventListener(e, resetTimer));
    window.addEventListener("storage", onStorageChange);
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      clearInterval(heartbeatId);
      events.forEach(e => window.removeEventListener(e, resetTimer));
      window.removeEventListener("storage", onStorageChange);
    };
  }, [user]);

  // Set up WebSocket connection when user is logged in
  useEffect(() => {
    if (user) {
      setupWebSocket();
    }

    // Cleanup on unmount or user logout
    return () => {
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }
    };
  }, [user, setupWebSocket]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        refetchUser,
        template,
        setTemplate,
        isAuthLoading,
        authError,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
